import { buttonClasses, createTheme, inputBaseClasses, outlinedInputClasses } from "@mui/material"
import "@fontsource-variable/noto-sans-sc"
import "@fontsource/poppins"
import "@fontsource/poppins/500.css"
import "@fontsource/poppins/600.css"

let baseTheme = createTheme({
  shape: { borderRadius: 8 },
  palette: {
    primary: {
      main: '#B30000',
      border: '#e60000'
    },
    success: { main: '#487131' },
    warning: { main: '#A88400' },
    error: { main: '#8A3422' },
    info: { main: '#077FA1' },
    text: {
      primary: '#2d2d2d',
      secondary: '#808080'
    },
    divider: '#8d8d8d'
  },
  transitions: {
    duration: {
      standard: 400,
    }
  }
})

baseTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontFamily: 'Poppins,  sans-serif',
      color: '#ff0000',
      fontSize: '1.3125rem',
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '1.05rem'
      },
      fontWeight: 600,
      lineHeight: 1.5,
      transition: 'font-size 400ms ease'
    },
    h2: {
      fontFamily: 'Poppins, sans-serif'
    },
    h3: {
      fontFamily: 'Poppins, sans-serif'
    },
    h4: {
      fontFamily: 'Poppins,  sans-serif',
      fontSize: '1.3125rem',
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '1.05rem'
      },
      fontWeight: 600,
      lineHeight: 1.5,
      transition: 'font-size 400ms ease'
    },
    h5: {
      fontFamily: 'Poppins, sans-serif'
    },
    h6: {
      fontFamily: 'Poppins, sans-serif'
    },
    body1: {
      fontFamily: 'Inter, sans-serif',
    },
    body2: {
      fontWeight: 500
    },
    caption: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      fontStyle: 'normal',
      textTransform: 'none',
      fontFamily: 'Inter, sans-serif',
      letterSpacing: 'normal'
    },
    footer: {
      fontSize: '0.875rem',
      color: baseTheme.palette.text.primary,
      fontWeight: 400,
      lineHeight: 1.5,
      fontStyle: 'normal',
      fontFamily: 'Inter, sans-serif',
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '.75rem',
        lineHeight: 1.25,
      },
    }
  },
})

export const nestedTheme = createTheme(baseTheme, {
  components: {
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          fontFamily: 'Inter, sans-serif',
          lineHeight: 'normal'
        }
      }

    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          height: 44,
          fontWeight: 500,
          fontSize: '1rem',
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 10,
          padding: '0 .5rem',
        },
        outlined: {
          [`&.${buttonClasses.colorPrimary}:not(.Mui-disabled)`]: {
            borderColor: '#b30000',
            '&:hover': {
              backgroundColor: '#fff2f2'
            }
          }
        },
        contained: {
          boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
          '&:not(.Mui-disabled):hover': {
            boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',

          },
          [`&.${buttonClasses.colorPrimary}:not(.Mui-disabled):hover`]: {
            backgroundColor: '#cc0000'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          lineHeight: 1.66,
          color: baseTheme.palette.text.secondary,
          margin: 0
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        adornedEnd: {
          paddingRight: 7
        }
      }
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      },
      styleOverrides: {
        root: {
          transform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: 5,
          transition: 'padding-left 0.3s',
          color: baseTheme.palette.text.primary,
          position: 'relative',
          '&.Mui-required': {
            paddingLeft: 13,
            '&::before': {
              position: 'absolute',
              left: -2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              content: '"✱"',
              color: baseTheme.palette.primary.main,
              overflow: 'hidden',
              opacity: 1,
              transform: 'rotate(0deg) scale(1)',
              transition: 'opacity 0.3s, transform 0.3s',
            },
            '&.Era-dirty': {
              paddingLeft: 0,
              '&:before': {
                opacity: 0,
                transform: 'rotate(90deg) scale(2)',
              }
            }
          },
        },
        asterisk: {
          display: 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          display: 'none'
        },
        root: {
          border: `1px solid ${baseTheme.palette.divider}`,
          borderRadius: baseTheme.shape.borderRadius,
          transition: 'all 200ms cubic-bezier(0, 0, 0.2, 1)',
          [`&.${inputBaseClasses.adornedEnd}`]: {
            paddingRight: 7
          },
          '&:not(.Mui-disabled)': {
            '&.Mui-focused': {
              borderColor: baseTheme.palette.primary.main,
              outline: `1px solid ${baseTheme.palette.primary.main}`,
              outlineOffset: 0
            },
          },
          '&.Mui-disabled': {
            color: baseTheme.palette.text.disabled,
            backgroundColor: '#f9f9f9',
            borderColor: '#d9d9d9',
          }
        },
        input: {
          height: 45,
          padding: '12px 14px',
          transition: 'all 200ms cubic-bezier(0, 0, 0.2, 1)',
          boxSizing: 'border-box',
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          position: 'relative',
          [`&:hover .${outlinedInputClasses.root}:not(.Mui-disabled)`]: {
            '&:not(.Mui-focused)': {
              borderColor: baseTheme.palette.common.black
            }
          },
        }
      }
    }
  }
})